import React, { useRef, useState } from 'react';
import { Button, Space } from 'antd';
import { Drawer, Modal, PrimaryButton } from '@common/components';
import { CopyShiftTimesFromUnitSelector } from '@scheduling/components';
import { CopyShiftTimesToUnitsSelector } from '@scheduling/components/admin-organization-unit-shift-configs/copy-shift-times-drawer/copy-shift-times-to-units-selector';
import { gql, TypedDocumentNode } from '@common/hooks';
import { ShiftTimesListQueryCgResponse } from '@common/graphql';
import { ShiftTimesListQueryVariables } from '@scheduling/graphql';

export const ORGANIZATION_UNITS_QUERY = gql`
  query Units {
    organizationUnits {
      id
      name
      costCenter
    }
  }
`;

export type UnitsQueryResponse = {
  organizationUnits: {
    id: string;
    name: string;
    costCenter: string;
  }[];
};

export const SHIFT_TIMES_QUERY: TypedDocumentNode<
  ShiftTimesListQueryCgResponse,
  ShiftTimesListQueryVariables
> = gql`
  query ShiftTimes($input: ShiftTimesListInput!) {
    shiftTimesList(input: $input) {
      collection {
        id
        startTime
        endTime
        workloadStartTime
        workloadEndTime
        duration
        role {
          id
          abbreviation
        }
        workforceGroups {
          id
          name
        }
      }
    }
  }
`;

export type Unit = UnitsQueryResponse['organizationUnits'][0];

type Props = {
  onClose: () => void;
  open: boolean;
  type?: 'from' | 'to';
  unitId?: string;
};

export function CopyShiftTimesDrawer({ open, onClose, type, unitId }: Props) {
  const selectorRef = useRef<{ handleConfirm: () => void } | null>(null);
  const [loading, setLoading] = useState(false);
  const [disabled, setDisabled] = useState(false);
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);

  if (!unitId) {
    return null;
  }

  const confirm = () => {
    selectorRef.current?.handleConfirm();
  };

  const title = {
    from: 'Copy From',
    to: 'Copy To',
  }[type || 'from'];

  return (
    <>
      <Modal
        title="Are you sure you want to copy shift times?"
        open={confirmModalOpen}
        onCancel={() => setConfirmModalOpen(false)}
        okText="Yes"
        onOk={confirm}
        confirmLoading={loading}
      >
        Any existing shift times will be overridden and replaced. This action
        cannot be undone.
      </Modal>
      <Drawer
        title={title}
        open={open}
        onClose={onClose}
        size="lg"
        footer={
          <Space>
            <Button size="large" key="back" onClick={() => onClose()}>
              Cancel
            </Button>
            <PrimaryButton
              size="large"
              onClick={() => setConfirmModalOpen(true)}
              disabled={disabled}
            >
              Confirm
            </PrimaryButton>
          </Space>
        }
      >
        {
          {
            from: (
              <CopyShiftTimesFromUnitSelector
                ref={selectorRef}
                unitId={unitId}
                setLoading={setLoading}
                closeDrawer={onClose}
                setDisabled={setDisabled}
              />
            ),
            to: (
              <CopyShiftTimesToUnitsSelector
                ref={selectorRef}
                unitId={unitId}
                setLoading={setLoading}
                closeDrawer={onClose}
                setDisabled={setDisabled}
              />
            ),
          }[type || 'from']
        }
      </Drawer>
    </>
  );
}
